import { Icon, TextField, InputAdornment, Checkbox } from "@mui/material";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import * as XLSX from "xlsx";

export default function FieldOptionsComponent({
  optionsList,
  setOptionsList,
  syncOptionsToString,
  setOptionName,
  setOptionValue,
  setOpenOptionDialog,
  setEditingOptionIndex,
  readOptionsCSV,
  formFieldEditable,
  setFormFieldEditable,
  isEdit = false,
}) {
  async function parseOptionsFile(file) {
    const fileExt = file.name.split(".").pop().toLowerCase();

    if (fileExt === "csv") {
      const text = await file.text();
      const lines = text.split("\n").filter((l) => l.trim().length > 0);
      return lines
        .map((line, idx) => {
          const cols = line.split(",");
          const name = cols[0]?.trim();
          const value = cols[1]?.trim() || name;

          if (!name) return null;

          return { name, value };
        })
        .filter(Boolean);
    }

    // Excel (xls, xlsx)
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);

    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    return json
      .map((row) => {
        const name = row[0]?.toString().trim();
        const value = row[1]?.toString().trim() || name;

        if (!name) return null;

        return { name, value };
      })
      .filter(Boolean);
  }

  return (
    <MDBox mt={2}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="h6" fontSize={14}>
          Field Options
        </MDTypography>

        <MDButton
          size="small"
          variant="outlined"
          color="dark"
          iconOnly
          onClick={() => {
            setOptionName("");
            setOptionValue("");
            setEditingOptionIndex(null);
            setOpenOptionDialog(true);
          }}
        >
          <Icon>add</Icon>
        </MDButton>
      </MDBox>

      {optionsList.length === 0 && (
        <MDBox
          mt={2}
          p={2}
          sx={{
            border: "1px dashed #ccc",
            borderRadius: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            setOptionName("");
            setOptionValue("");
            setEditingOptionIndex(null);
            setOpenOptionDialog(true);
          }}
        >
          <MDTypography variant="button" align="center" display="block">
            Click to add options
          </MDTypography>
        </MDBox>
      )}

      {optionsList.map((opt, idx) => (
        <MDBox
          key={idx}
          mt={0.5}
          px={1}
          sx={{
            border: "1px solid #eee",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <MDTypography variant="caption">
            {opt.name} | {opt.value || opt.name}
          </MDTypography>

          <MDBox>
            {isEdit && (
              <MDButton
                iconOnly
                size="small"
                onClick={() => {
                  setOptionName(opt.name);
                  setOptionValue(opt.value);
                  setEditingOptionIndex(idx);
                  setOpenOptionDialog(true);
                }}
              >
                <Icon color="warning">edit</Icon>
              </MDButton>
            )}

            <MDButton
              iconOnly
              size="small"
              onClick={() => {
                const tmp = optionsList.filter((_, i) => i !== idx);
                setOptionsList(tmp);
                syncOptionsToString(tmp);
              }}
            >
              <Icon color="error">delete</Icon>
            </MDButton>
          </MDBox>
        </MDBox>
      ))}

      <MDBox mt={2}>
        <MDTypography variant="h6" fontWeight="light" fontSize={13} pl={0.5}>
          Upload CSV file
        </MDTypography>

        <TextField
          type="file"
          size="small"
          onChange={async (e) => {
            const file = e.target.files[0];
            try {
              const arr = await parseOptionsFile(file);
              setOptionsList(arr);
              syncOptionsToString(arr);
            } catch (error) {
              console.error(error);
            }
          }}
          startAdornment={
            <InputAdornment position="start">
              <Icon color="info">file</Icon>
            </InputAdornment>
          }
          fullWidth
        />
      </MDBox>

      <MDBox mt={2} display="flex" alignItems="center" ml={-1}>
        <Checkbox
          checked={formFieldEditable}
          onChange={(e) => setFormFieldEditable(e.target.checked)}
        />
        <MDTypography variant="button" sx={{ ml: -1 }}>
          &nbsp;&nbsp;Allow users enter preferred option?
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}
